import { IResult } from '../models/HttpResult';
import { IPagedResult } from '../models/HttpPagedResult';
import { PaginationState } from '../models/PaginationState';
import {
    ExplainSettlementRegressionsCommand,
    SetRegressionCauseDecisionCommand,
    SettlementRegressionAffectedConsumption,
    SettlementRegressionExplanation,
    SettlementRegressionExplanationListItem,
} from '../models/Entities/Protocols/SettlementRegression';
import { buildHeaders, handleApiError } from './commons/CommonService.Functions';

const Gateway = (process.env.REACT_APP_BILLER_API_GATEWAY || '').replace(/\/$/, '');
const ProtocolsURL = `${Gateway}/protocols/protocols`;
const RegressionsURL = `${ProtocolsURL}/settlement-regressions`;

export const REGRESSION_DEFAULT_PAGE_SIZE = 10;

export interface PagedItems<T> {
    items: T[];
    paginationState: PaginationState;
}

const toPage = <T>(data: IPagedResult<T[]>): PagedItems<T> => ({
    items: data.data || [],
    paginationState: {
        pageNumber: data.pageNumber,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
        totalRecords: data.totalRecords,
        firstPage: data.firstPage,
        lastPage: data.lastPage,
        nextPage: data.nextPage,
        previousPage: data.previousPage,
    },
});

/**
 * Pide la explicación de un protocolo (POST .../{protocolId}/settlement-regressions/explanations).
 * Se ejecuta en línea: responde cuando terminó, con el id de la explicación. Si ya
 * hay una en curso el servidor la rechaza, y eso llega acá como error.
 */
export const ExplainSettlementRegressions = async (
    command: ExplainSettlementRegressionsCommand,
    token: string
): Promise<string> => {
    const url = `${ProtocolsURL}/${command.protocolId}/settlement-regressions/explanations`;
    try {
        const response = await fetch(url, { method: 'POST', headers: buildHeaders(token), body: JSON.stringify(command) });
        if (!response.ok) throw response;
        const data: IResult<string> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'ExplainSettlementRegressions');
    }
};

/** Explicaciones pedidas sobre un protocolo, más recientes primero (GET .../{protocolId}/settlement-regressions/explanations). */
export const GetSettlementRegressionExplanations = async (
    protocolId: number,
    token: string,
    pageNumber: number = 1,
    pageSize: number = REGRESSION_DEFAULT_PAGE_SIZE
): Promise<PagedItems<SettlementRegressionExplanationListItem>> => {
    const url = `${ProtocolsURL}/${protocolId}/settlement-regressions/explanations?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (!response.ok) throw response;
        return toPage<SettlementRegressionExplanationListItem>(await response.json());
    } catch (error: any) {
        throw await handleApiError(error, 'GetSettlementRegressionExplanations');
    }
};

/** Una explicación con su estado, su resumen y sus causas (GET .../explanations/{explanationId}). */
export const GetSettlementRegressionExplanation = async (
    explanationId: string,
    token: string
): Promise<SettlementRegressionExplanation> => {
    const url = `${RegressionsURL}/explanations/${encodeURIComponent(explanationId)}`;
    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (!response.ok) throw response;
        const data: IResult<SettlementRegressionExplanation> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'GetSettlementRegressionExplanation');
    }
};

/**
 * Reintenta sólo la redacción de una explicación que quedó sin redactar (IA
 * apagada o caída). No recalcula causas ni consumos (POST .../explanations/{id}/narrative).
 */
export const RetrySettlementRegressionNarrative = async (explanationId: string, token: string): Promise<string> => {
    const url = `${RegressionsURL}/explanations/${encodeURIComponent(explanationId)}/narrative`;
    try {
        const response = await fetch(url, { method: 'POST', headers: buildHeaders(token) });
        if (!response.ok) throw response;
        const data: IResult<string> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'RetrySettlementRegressionNarrative');
    }
};

/** Consumos que dejaron de generarse por una causa, paginados (GET .../causes/{causeId}/consumptions). */
export const GetSettlementRegressionCauseConsumptions = async (
    causeId: string,
    token: string,
    pageNumber: number = 1,
    pageSize: number = REGRESSION_DEFAULT_PAGE_SIZE
): Promise<PagedItems<SettlementRegressionAffectedConsumption>> => {
    const url = `${RegressionsURL}/causes/${encodeURIComponent(causeId)}/consumptions?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (!response.ok) throw response;
        return toPage<SettlementRegressionAffectedConsumption>(await response.json());
    } catch (error: any) {
        throw await handleApiError(error, 'GetSettlementRegressionCauseConsumptions');
    }
};

/** Marca una causa como revisada o descartada, con motivo (PUT .../causes/{causeId}/decision). */
export const SetSettlementRegressionCauseDecision = async (
    command: SetRegressionCauseDecisionCommand,
    token: string
): Promise<string> => {
    const url = `${RegressionsURL}/causes/${encodeURIComponent(command.causeId)}/decision`;
    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: buildHeaders(token),
            body: JSON.stringify({ ...command, reason: command.reason || null }),
        });
        if (!response.ok) throw response;
        const data: IResult<string> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'SetSettlementRegressionCauseDecision');
    }
};
